<template>
  <main class="analytics-page">
    <header class="analytics-hero">
      <div>
        <span class="analytics-eyebrow">Medhop Intelligence</span>
        <h1>Ringkasan Eksekutif &amp; Konsolidasi</h1>
        <p>Pantau kesehatan bisnis, layanan pasien, dan kualitas data {{ scopeDescription }} dalam satu cockpit keputusan.</p>
        <div class="analytics-scope">
          <i :class="isConsolidated ? 'fas fa-layer-group' : 'fas fa-map-marker-alt'"></i>
          <strong>{{ scopeLabel }}</strong>
          <span>{{ branchCountLabel }}</span>
        </div>
      </div>
      <div class="analytics-hero__actions">
        <button class="analytics-button analytics-button--ghost" type="button" :disabled="loading || !trend.length" @click="exportCsv">
          <i class="fas fa-file-download"></i> Ekspor CSV
        </button>
        <button class="analytics-button analytics-button--primary" type="button" :disabled="loading" @click="load">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> Perbarui
        </button>
      </div>
    </header>

    <section class="analytics-filter" aria-label="Filter periode">
      <div class="analytics-presets">
        <button v-for="preset in presets" :key="preset.key" type="button"
          :class="{ active: activePreset === preset.key }" @click="applyPreset(preset.key)">
          {{ preset.label }}
        </button>
      </div>
      <div class="analytics-dates">
        <label>Dari<input v-model="filters.start_date" type="date" :max="filters.end_date" @change="activePreset = 'custom'"></label>
        <label>Sampai<input v-model="filters.end_date" type="date" :min="filters.start_date" :max="today" @change="activePreset = 'custom'"></label>
        <button class="analytics-button analytics-button--primary" type="button" :disabled="loading || !validPeriod" @click="load">Terapkan</button>
      </div>
    </section>

    <div v-if="errorMessage" class="analytics-alert" role="alert">
      <i class="fas fa-exclamation-circle"></i>
      <div><strong>Data belum dapat dimuat</strong><span>{{ errorMessage }}</span></div>
      <button type="button" @click="load">Coba lagi</button>
    </div>

    <template v-if="loading && !loaded">
      <div class="analytics-skeleton-grid"><span v-for="item in 6" :key="item"></span></div>
      <div class="analytics-skeleton-panel"></div>
    </template>

    <template v-else>
      <section class="analytics-kpis" aria-label="Indikator utama">
        <article v-for="card in kpiCards" :key="card.key" class="analytics-kpi">
          <div class="analytics-kpi__head">
            <span :class="'analytics-kpi__icon analytics-kpi__icon--' + card.tone"><i :class="card.icon"></i></span>
            <span v-if="kpi(card.key)" class="analytics-growth" :class="growthClass(card)">
              <i :class="growthIcon(card)"></i>{{ absoluteGrowth(card) }}%
            </span>
          </div>
          <span class="analytics-kpi__label">{{ card.label }}</span>
          <strong>{{ formatMetric(card) }}</strong>
          <small>{{ card.context }}</small>
        </article>
      </section>

      <section v-if="branchPerformance.length" class="analytics-card analytics-branches">
        <div class="analytics-card__head">
          <div>
            <span class="analytics-eyebrow">Network performance</span>
            <h2>{{ isConsolidated ? "Perbandingan kinerja cabang" : "Kinerja cabang aktif" }}</h2>
          </div>
          <span class="analytics-period">{{ branchPerformance.length }} cabang · {{ periodLabel }}</span>
        </div>
        <div class="analytics-branch-table">
          <div class="analytics-branch-row analytics-branch-row--head">
            <span>Cabang</span><span>Pendapatan</span><span>Hasil operasional</span><span>Kunjungan</span><span>Pasien baru</span><span>No-show</span>
          </div>
          <div v-for="branch in branchPerformance" :key="branch.id" class="analytics-branch-row">
            <div class="analytics-branch-name">
              <span class="analytics-rank">#{{ branch.rank }}</span>
              <div><strong>{{ branch.name }}</strong><small>{{ branch.code }}<template v-if="branch.is_head_office"> · Pusat</template></small></div>
            </div>
            <div class="analytics-branch-metric">
              <strong>{{ formatCurrency(branch.revenue) }}</strong>
              <span class="analytics-growth" :class="safeNumber(branch.revenue_growth) >= 0 ? 'positive' : 'negative'">
                <i :class="safeNumber(branch.revenue_growth) >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>{{ formatNumber(Math.abs(safeNumber(branch.revenue_growth)), 1) }}%
              </span>
              <div class="analytics-branch-share"><span :style="{ width: branch.revenue_share + '%' }"></span></div>
              <small>{{ formatNumber(branch.revenue_share, 1) }}% kontribusi jaringan</small>
            </div>
            <div class="analytics-branch-metric"><strong :class="{ 'analytics-negative': safeNumber(branch.operating_result) < 0 }">{{ formatCurrency(branch.operating_result) }}</strong><small>Margin {{ formatNumber(branch.operating_margin, 1) }}%</small></div>
            <div class="analytics-branch-metric"><strong>{{ formatNumber(branch.visits) }}</strong><small>{{ formatCurrency(branch.revenue_per_visit) }} / kunjungan</small></div>
            <div class="analytics-branch-metric"><strong>{{ formatNumber(branch.new_patients) }}</strong><small>Pasien terdaftar</small></div>
            <div class="analytics-branch-metric"><strong :class="{ 'analytics-negative': safeNumber(branch.no_show_rate) >= 10 }">{{ formatNumber(branch.no_show_rate, 1) }}%</strong><small>Selesai {{ formatNumber(branch.completion_rate, 1) }}%</small></div>
          </div>
        </div>
      </section>

      <section class="analytics-grid analytics-grid--wide">
        <article class="analytics-card analytics-card--trend">
          <div class="analytics-card__head">
            <div><span class="analytics-eyebrow">Tren harian</span><h2>Pendapatan, biaya &amp; kunjungan</h2></div>
            <span class="analytics-period">{{ periodLabel }}</span>
          </div>
          <div v-if="hasTrendData" class="analytics-chart">
            <apexchart type="line" height="330" :options="trendOptions" :series="trendSeries"></apexchart>
          </div>
          <div v-else class="analytics-empty"><i class="fas fa-chart-line"></i><strong>Belum ada aktivitas</strong><span>Data tren akan tampil setelah transaksi atau kunjungan tercatat.</span></div>
        </article>

        <aside class="analytics-card analytics-insights">
          <div class="analytics-card__head"><div><span class="analytics-eyebrow">Decision support</span><h2>Insight prioritas</h2></div><i class="fas fa-lightbulb"></i></div>
          <div v-for="(insight, index) in insights" :key="index" class="analytics-insight" :class="'analytics-insight--' + insight.severity">
            <span><i :class="insightIcon(insight.severity)"></i></span>
            <div><strong>{{ insight.title }}</strong><p>{{ insight.message }}</p></div>
          </div>
        </aside>
      </section>

      <section class="analytics-grid analytics-grid--thirds">
        <article class="analytics-card">
          <div class="analytics-card__head"><div><span class="analytics-eyebrow">Revenue mix</span><h2>Sumber pendapatan</h2></div><i class="fas fa-wallet"></i></div>
          <div v-if="revenueMix.length" class="analytics-mix">
            <div v-for="item in revenueMix" :key="item.key">
              <div><span>{{ item.label }}</span><strong>{{ formatCurrency(item.value) }}</strong></div>
              <div class="analytics-progress"><span :style="{ width: item.percentage + '%' }"></span></div>
              <small>{{ formatNumber(item.percentage, 1) }}% dari pendapatan</small>
            </div>
          </div>
        </article>

        <article class="analytics-card">
          <div class="analytics-card__head"><div><span class="analytics-eyebrow">Patient flow</span><h2>Status kunjungan</h2></div><i class="fas fa-user-clock"></i></div>
          <div v-if="visitStatuses.length" class="analytics-statuses">
            <div v-for="status in visitStatuses.slice(0, 6)" :key="status.label">
              <span>{{ status.label }}</span><strong>{{ formatNumber(status.value) }}</strong>
            </div>
          </div>
          <div v-else class="analytics-empty analytics-empty--small">Belum ada jadwal pada periode ini.</div>
        </article>

        <article class="analytics-card">
          <div class="analytics-card__head"><div><span class="analytics-eyebrow">Data trust</span><h2>Kualitas data</h2></div><i class="fas fa-shield-alt"></i></div>
          <div class="analytics-quality">
            <div class="analytics-quality__score" :class="{ warning: dataQuality.score < 95 }">{{ formatNumber(dataQuality.score, 1) }}<small>/100</small></div>
            <p>{{ qualityMessage }}</p>
            <div><span>Record diperiksa</span><strong>{{ formatNumber(dataQuality.total_records) }}</strong></div>
            <div><span>Atribut belum lengkap</span><strong>{{ formatNumber(dataQuality.issues) }}</strong></div>
          </div>
        </article>
      </section>

      <section class="analytics-card analytics-doctors">
        <div class="analytics-card__head"><div><span class="analytics-eyebrow">Service performance</span><h2>Dokter dengan kunjungan selesai terbanyak</h2></div><span class="analytics-period">Top 5</span></div>
        <div v-if="topDoctors.length" class="analytics-doctor-list">
          <div v-for="(doctor, index) in topDoctors" :key="doctor.doctor_id || index">
            <span class="analytics-rank">{{ index + 1 }}</span>
            <span class="analytics-avatar">{{ initials(doctor.name) }}</span>
            <div><strong>{{ doctor.name }}</strong><small>Kunjungan selesai</small></div>
            <strong>{{ formatNumber(doctor.visits) }}</strong>
          </div>
        </div>
        <div v-else class="analytics-empty analytics-empty--small">Belum ada kunjungan selesai pada periode ini.</div>
      </section>

      <footer class="analytics-footer">
        <span><i class="fas fa-lock"></i> Data terisolasi untuk klinik ini</span>
        <span v-if="generatedAt">Diperbarui {{ generatedAt }}</span>
      </footer>
    </template>
  </main>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

export default {
  name: "ReportAnalytics",
  data() {
    return {
      loading: false, loaded: false, errorMessage: "", activePreset: "month",
      filters: { start_date: "", end_date: "" },
      presets: [
        { key: "today", label: "Hari ini" }, { key: "week", label: "7 hari" },
        { key: "month", label: "Bulan ini" }, { key: "quarter", label: "90 hari" },
      ],
      kpis: {}, trend: [], revenueMix: [], visitStatuses: [], topDoctors: [], insights: [], branchPerformance: [],
      scope: { mode: "branch", selected_branch_id: null, branch_count: 0, branches: [] },
      dataQuality: { score: 100, total_records: 0, issues: 0 }, period: {}, generatedAt: "",
      kpiCards: [
        { key: "revenue", label: "Pendapatan bersih", icon: "fas fa-coins", tone: "blue", type: "currency", context: "Setelah refund" },
        { key: "operating_result", label: "Hasil operasional", icon: "fas fa-chart-line", tone: "teal", type: "currency", context: "Pendapatan dikurangi biaya" },
        { key: "visits", label: "Kunjungan selesai", icon: "fas fa-user-check", tone: "purple", type: "number", context: "Pasien telah dilayani" },
        { key: "new_patients", label: "Pasien baru", icon: "fas fa-user-plus", tone: "orange", type: "number", context: "Terdaftar di periode ini" },
        { key: "revenue_per_visit", label: "Pendapatan / kunjungan", icon: "fas fa-receipt", tone: "blue", type: "currency", context: "Nilai rata-rata layanan" },
        { key: "no_show_rate", label: "Tingkat no-show", icon: "fas fa-calendar-times", tone: "red", type: "percent", context: "Target ideal di bawah 10%", inverse: true },
      ],
    };
  },
  computed: {
    today() { return this.dateString(new Date()); },
    isConsolidated() { return this.scope.mode === "consolidated"; },
    scopeLabel() { return this.isConsolidated ? "Seluruh cabang" : ((this.scope.branches || [])[0] || {}).name || "Cabang aktif"; },
    scopeDescription() { return this.isConsolidated ? "lintas seluruh cabang" : "untuk cabang aktif"; },
    branchCountLabel() { const count = this.safeNumber(this.scope.branch_count); return count + (count === 1 ? " cabang aktif" : " cabang terkonsolidasi"); },
    validPeriod() { return this.filters.start_date && this.filters.end_date && this.filters.start_date <= this.filters.end_date; },
    periodLabel() { return this.period.start_date ? this.formatDate(this.period.start_date) + " – " + this.formatDate(this.period.end_date) : "Periode aktif"; },
    hasTrendData() { return this.trend.some(item => this.safeNumber(item.revenue) || this.safeNumber(item.expense) || this.safeNumber(item.visits)); },
    qualityMessage() { return this.dataQuality.score >= 95 ? "Data cukup lengkap untuk pengambilan keputusan." : "Lengkapi data dokter dan pasien agar analitik lebih akurat."; },
    trendSeries() {
      return [
        { name: "Pendapatan", type: "column", data: this.trend.map(item => this.safeNumber(item.revenue)) },
        { name: "Pengeluaran", type: "column", data: this.trend.map(item => this.safeNumber(item.expense)) },
        { name: "Kunjungan", type: "line", data: this.trend.map(item => this.safeNumber(item.visits)) },
      ];
    },
    trendOptions() {
      const vm = this;
      const currencyMax = Math.max(1, ...this.trend.map(item => Math.max(this.safeNumber(item.revenue), this.safeNumber(item.expense))));
      const visitMax = Math.max(1, ...this.trend.map(item => this.safeNumber(item.visits)));
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
        colors: ["var(--skin-primary)", "#e98645", "#18a29b"], stroke: { width: [0, 0, 3], curve: "smooth" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "58%" } }, dataLabels: { enabled: false },
        grid: { borderColor: "#edf1f5", strokeDashArray: 4 }, legend: { position: "top", horizontalAlign: "right" },
        xaxis: { categories: this.trend.map(item => this.formatDate(item.date, true)), labels: { rotate: -35, hideOverlappingLabels: true } },
        yaxis: [
          { seriesName: "Pendapatan", min: 0, max: currencyMax, tickAmount: 4, labels: { style: { fontSize: "12px" }, formatter(value) { return vm.compactCurrency(value); } } },
          { seriesName: "Pengeluaran", show: false, min: 0, max: currencyMax, tickAmount: 4 },
          { seriesName: "Kunjungan", opposite: true, min: 0, max: visitMax, tickAmount: Math.min(4, Math.ceil(visitMax)), forceNiceScale: true, decimalsInFloat: 0, labels: { style: { fontSize: "12px" }, formatter(value) { return Math.round(vm.safeNumber(value)); } } },
        ],
        tooltip: { shared: true, y: { formatter(value, ctx) { return ctx.seriesIndex === 2 ? vm.formatNumber(value) + " kunjungan" : vm.formatCurrency(value); } } },
      };
    },
  },
  created() { this.applyPreset("month", false); },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Laporan & Analitik" }, { title: "Pusat Analitik" }]);
    this.load();
  },
  methods: {
    kpi(key) { return this.kpis[key] || null; },
    applyPreset(key, reload = true) {
      const end = new Date(); const start = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      if (key === "week") start.setDate(start.getDate() - 6);
      if (key === "month") start.setDate(1);
      if (key === "quarter") start.setDate(start.getDate() - 89);
      this.filters = { start_date: this.dateString(start), end_date: this.dateString(end) };
      this.activePreset = key; if (reload) this.load();
    },
    async load() {
      if (!this.validPeriod || this.loading) return;
      this.loading = true; this.errorMessage = "";
      try {
        const response = await ApiService.query("report-analytics/overview", { params: this.filters });
        if (!response || response.status >= 400) throw new Error(response && response.data && response.data.message);
        const data = response.data.data || {};
        this.kpis = data.kpis || {}; this.trend = data.trend || []; this.revenueMix = data.revenue_mix || [];
        this.visitStatuses = data.visit_statuses || []; this.topDoctors = data.top_doctors || []; this.insights = data.insights || [];
        this.scope = data.scope || this.scope; this.branchPerformance = data.branch_performance || [];
        this.dataQuality = data.data_quality || { score: 100, total_records: 0, issues: 0 }; this.period = data.period || {};
        this.generatedAt = data.generated_at ? new Intl.DateTimeFormat("id-ID", { dateStyle: "medium", timeStyle: "short" }).format(new Date(data.generated_at)) : "";
        this.loaded = true;
      } catch (error) {
        this.errorMessage = (error && error.message) || "Periksa koneksi dan coba kembali.";
      } finally { this.loading = false; }
    },
    formatMetric(card) {
      const metric = this.kpi(card.key); const value = metric ? metric.value : 0;
      if (card.type === "currency") return this.formatCurrency(value);
      if (card.type === "percent") return this.formatNumber(value, 1) + "%";
      return this.formatNumber(value);
    },
    safeNumber(value) { const number = Number(value); return Number.isFinite(number) ? number : 0; },
    formatCurrency(value) { return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(this.safeNumber(value)); },
    compactCurrency(value) { return "Rp" + new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(this.safeNumber(value)); },
    formatNumber(value, digits = 0) { return new Intl.NumberFormat("id-ID", { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(this.safeNumber(value)); },
    dateString(date) { const year = date.getFullYear(); const month = String(date.getMonth() + 1).padStart(2, "0"); const day = String(date.getDate()).padStart(2, "0"); return year + "-" + month + "-" + day; },
    formatDate(value, short = false) {
      const parts = String(value || "").split("-"); const date = new Date(this.safeNumber(parts[0]), this.safeNumber(parts[1]) - 1, this.safeNumber(parts[2]));
      return Number.isNaN(date.getTime()) ? "-" : new Intl.DateTimeFormat("id-ID", short ? { day: "2-digit", month: "short" } : { day: "numeric", month: "short", year: "numeric" }).format(date);
    },
    growthClass(card) { const growth = this.safeNumber((this.kpi(card.key) || {}).growth); return (card.inverse ? growth <= 0 : growth >= 0) ? "positive" : "negative"; },
    growthIcon(card) { return this.safeNumber((this.kpi(card.key) || {}).growth) >= 0 ? "fas fa-arrow-up" : "fas fa-arrow-down"; },
    absoluteGrowth(card) { return this.formatNumber(Math.abs(this.safeNumber((this.kpi(card.key) || {}).growth)), 1); },
    insightIcon(severity) { return { critical: "fas fa-exclamation-triangle", warning: "fas fa-bell", positive: "fas fa-chart-line", info: "fas fa-info-circle" }[severity] || "fas fa-info-circle"; },
    initials(name) { return String(name || "D").split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase(); },
    exportCsv() {
      const rows = [["Ringkasan tren"], ["Tanggal", "Pendapatan", "Pengeluaran", "Kunjungan"]]
        .concat(this.trend.map(item => [item.date, item.revenue, item.expense, item.visits]))
        .concat([[""], ["Performa cabang"], ["Peringkat", "Cabang", "Pendapatan", "Pertumbuhan (%)", "Hasil operasional", "Margin (%)", "Kunjungan", "Pasien baru", "No-show (%)"]])
        .concat(this.branchPerformance.map(item => [item.rank, item.name, item.revenue, item.revenue_growth, item.operating_result, item.operating_margin, item.visits, item.new_patients, item.no_show_rate]));
      const csv = "\uFEFF" + rows.map(row => row.map(value => '"' + String(value).replace(/"/g, '""') + '"').join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a"); link.href = url; link.download = "medhop-analitik-" + this.filters.start_date + "-" + this.filters.end_date + ".csv";
      link.click(); URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_analytics-center.scss"></style>
